// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useState, useRef } from 'react';
// Auth0 integration for SSO authentication
import { useAuth0 } from '@auth0/auth0-react';

import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { Link, Typography, Button, CircularProgress, Fade, Collapse, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, useTheme } from '@mui/system';
import { Form } from 'react-final-form';
import history from 'Utils/history';
import { ROOT_ROUTE } from 'Utils/routes';
import { TextField, Validators, Helpers } from 'Components/forms';
import { clearLocalStorage } from 'Utils/localStorageService';
import { clearSessionStorage } from 'Utils/sessionStorageService';
import { withOktaAuth, useOktaAuth } from '@okta/okta-react';
import ReCAPTCHA from 'react-google-recaptcha';
import { FORM_ERROR } from 'final-form';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { API_URL } from 'Config';
// Import both Auth0 and standard login actions
import { Auth0Login, Login } from '../authActions';
import stylesDef from './styles';

const LoginForm = () => {
  const { failedLoginAttempts } = useAppSelector(
    (state) => state.auth,
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const styles = stylesDef(theme);
  const captchaRef = useRef(null);
  const { oktaAuth } = _ADMIN_ && useOktaAuth();
  // Auth0 hook for SSO redirect functionality
  const {
    loginWithRedirect,
  } = useAuth0();
  const [disableSubmit, setDisableSubmit] = useState(true);
  const [emailEntered, setEmailEntered] = useState('');
  const [showPasswordField, setShowPasswordField] = useState(false);
  // State to control Auth0 SSO login button display
  const [showAuth0Login, setShowAuth0Login] = useState(false);
  // Extract Auth0 error from navigation state if present
  const auth0Error = location.state?.auth0Error;

  const handleEmailSubmit = async (email: string) => {
    setEmailEntered(email);

    try {
      // Call API to check if this email's organization has SSO enabled
      const response = await fetch(`${API_URL}/users/checkSso`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();

      if (data.ssoEnabled) {
        // Show Auth0 SSO login if organization has SSO configured
        setShowAuth0Login(true);
        setShowPasswordField(false);
      } else {
        // Show standard password login for non-SSO organizations
        setShowPasswordField(true);
        setShowAuth0Login(false);
      }
    } catch (error) {
      // Default to password login if SSO check API call fails
      setShowPasswordField(true);
      setShowAuth0Login(false);
    }
  };

  const onLogin = () => {
    oktaAuth.signInWithRedirect({ originalUri: '/admin' });
  };

  useEffect(() => {
    if (failedLoginAttempts > 2) {
      captchaRef.current.reset();
      setDisableSubmit(true);
    }
  }, [failedLoginAttempts]);

  const onFormSubmit = async (values: { email: string; password: string; }) => {
    try {
      await dispatch(Login(values)).unwrap();
      navigate('/projects');
    } catch (error) {
      if (error.message) {
        return { [FORM_ERROR]: error.message };
      }
    }
  };

  useEffect(() => {
    const currentLocation = history.location.pathname;
    if (currentLocation === ROOT_ROUTE) {
      clearLocalStorage();
      clearSessionStorage();
    }
  }, []);

  return (
    <Box>
      <Typography sx={styles.header} variant="h1">
        {_ADMIN_ ? 'Airworks Admin Portal' : 'Log in to AirWorks'}
      </Typography>
      {!_ADMIN_ && (
        <Typography sx={styles.header} variant="h5">
          Don’t have an AirWorks account?
          {' '}
          <Link
            component={RouterLink}
            sx={styles.forgotText}
            to="/signUp"
          >
            Sign up now.
          </Link>
        </Typography>
      )}
      {_ADMIN_ ? (
        <Box sx={styles.buttonWrapper}>
          <Button
            sx={styles.buttonOkta}
            onClick={onLogin}
          >
            Click here for Okta login
          </Button>
        </Box>
      ) : (
        <Form
          onSubmit={showPasswordField ? onFormSubmit : () => {}}
          render={({ hasSubmitErrors, submitting, modifiedSinceLastSubmit, handleSubmit, submitError, values, form }) => (
            <>
              <form
                style={styles.form}
                onSubmit={handleSubmit}
              >
                {(showPasswordField || showAuth0Login) ? (
                  <Box sx={styles.emailSection}>
                    <IconButton
                      onClick={() => {
                        form.restart();
                        setEmailEntered('');
                        setShowPasswordField(false);
                        setShowAuth0Login(false); // Reset Auth0 login state
                        navigate(location.pathname, { replace: true });
                      }}
                      sx={styles.backButton}
                    >
                      <ArrowBackIcon />
                    </IconButton>
                    <Box sx={styles.emailWrapper}>
                      <Typography variant="body2" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body1" sx={styles.emailDisplay}>
                        {emailEntered}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <TextField
                    name="email"
                    label="Email Address"
                    autoComplete="email"
                    autoFocus
                    validate={Validators.composeValidators(Validators.required, Validators.email)}
                    onChange={(value: string) => {
                      if (value && value.includes('@') && !showPasswordField && !showAuth0Login) {
                        handleEmailSubmit(value);
                      }
                    }}
                  />
                )}
                {showPasswordField && (
                  <>
                    <TextField
                      name="password"
                      label="Password"
                      type="password"
                      validate={Validators.composeValidators(Validators.required, Validators.minLength7)}
                    />
                    <Typography variant="caption">
                      <Link component={RouterLink} sx={styles.forgotText} to="/forgot">Forgot Password?</Link>
                    </Typography>
                  </>
                )}
                {!showPasswordField && !showAuth0Login && values?.email && (
                  <Box sx={styles.buttonWrapper}>
                    <Button
                      variant="outlined"
                      sx={styles.button}
                      onClick={() => handleEmailSubmit(values.email)}
                    >
                      Continue
                    </Button>
                  </Box>
                )}
                <Collapse in={Boolean((submitError && !modifiedSinceLastSubmit))}>
                  <Typography variant="body1" sx={styles.errorMessage}>
                    {submitError}
                  </Typography>
                </Collapse>
                {failedLoginAttempts > 2 && (
                  <Box sx={styles.captcha}>
                    <ReCAPTCHA
                      sitekey={_GOOGLE_CAPTCHA_SITE_KEY_}
                      onChange={() => setDisableSubmit(false)}
                      ref={captchaRef}
                    />
                  </Box>
                )}
                {showPasswordField && (
                  <Box sx={styles.buttonWrapper}>
                    <Button
                      type="submit"
                      variant="outlined"
                      sx={styles.button}
                      disabled={(hasSubmitErrors && !modifiedSinceLastSubmit) || submitting || (failedLoginAttempts > 2 ? disableSubmit : false)}
                    >
                      <Fade in={submitting}><CircularProgress size={24} sx={styles.buttonProgress} /></Fade>
                      <Fade in={!submitting}><span>Sign in</span></Fade>
                    </Button>
                  </Box>
                )}
              </form>
              {/* Auth0 SSO login button - shown when organization has SSO enabled */}
              {showAuth0Login && (
                <Box sx={styles.buttonWrapper}>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={styles.button}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Initiate Auth0 redirect with email as login hint
                      loginWithRedirect({
                        authorizationParams: {
                          login_hint: emailEntered,
                        },
                      });
                    }}
                  >
                    Continue with SSO
                  </Button>
                </Box>
              )}
              {/* Display Auth0 authentication errors if any */}
              {auth0Error && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={styles.errorMessage}>
                    {auth0Error}
                  </Typography>
                </Box>
              )}
            </>
          )}
        />
      )}
    </Box>
  );
};

export default LoginForm;
